import {AlertTriangle} from 'lucide-react'
import {Link} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {cn} from '@/lib/utils'
import {ApiError} from '@/api/client'
import {getErrorMessage} from '@/lib/error-utils'
import {hasGlobalPermission} from '@/contexts/auth-context'
import {permissions} from '@/lib/permissions'
import {routes} from '@/lib/routes'
import {NotFoundError} from '@/lib/not-found-error'

interface ErrorBannerProps {
    error: Error | null | undefined
    className?: string
    onRetry?: () => void
}

export function ErrorBanner({error, className, onRetry}: ErrorBannerProps) {
    const {t} = useTranslation()
    if (!error) return null
    const message = error instanceof NotFoundError
        ? t('common.notFound', {entity: t(`appConfig.domainEntityTypes.${error.entityCode}`)})
        : getErrorMessage(error, t)
    const errorCode = error instanceof ApiError ? error.code : undefined
    const errorLogId = error instanceof ApiError ? error.backendErrorLogId : undefined
    return (
        <div className={cn('rounded-lg border border-destructive bg-destructive/10 p-4', className)}
             data-testid="error-banner"
             data-error-code={errorCode}>
            <div className="flex items-center gap-2 text-sm text-destructive">
                <AlertTriangle className="h-4 w-4 shrink-0"/>
                <div className="flex-1">
                    {errorCode && (
                        <span className="font-mono text-xs opacity-70 mr-2">{errorCode}</span>
                    )}
                    <span>{message}</span>
                    {errorLogId && hasGlobalPermission(permissions.errorLogs.view) && (
                        <Link
                            to={routes.admin.errorLogs.detail(errorLogId)}
                            className="ml-2 text-xs underline opacity-70 hover:opacity-100"
                            data-testid="error-log-link"
                        >
                            {t('common.viewErrorLog')}
                        </Link>
                    )}
                </div>
                {onRetry && (
                    <button
                        type="button"
                        onClick={onRetry}
                        className="shrink-0 text-xs font-medium underline hover:no-underline"
                        data-testid="error-retry"
                    >
                        {t('common.retry')}
                    </button>
                )}
            </div>
        </div>
    )
}
